import {
  Box,
  Center,
  HStack,
  Spinner,
  Tag,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useGetLunaRequests } from "state/luna";
import type { LunaRequest } from "state/luna";

const statusConfig: Record<
  LunaRequest["status"],
  { label: string; colorScheme: string }
> = {
  waiting_response: { label: "Waiting", colorScheme: "orange" },
  thinking: { label: "Thinking", colorScheme: "blue" },
  closed: { label: "Closed", colorScheme: "gray" },
};

const formatRelativeTime = (dateString: string): string => {
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const LunaRequestsPage = () => {
  const navigate = useNavigate();
  const { data: requests, isLoading } = useGetLunaRequests();

  return (
    <VStack w="full" align="flex-start" spacing={0}>
      <HStack w="full" px={4} minH="53px" align="center">
        <Text fontSize="lg" fontWeight="500" color="text.body.primary">
          Requests
        </Text>
      </HStack>

      <Box px={4} w="full" maxW="800px" pb={4}>
        {isLoading ? (
          <Center h="200px">
            <Spinner size="lg" color="p.600" />
          </Center>
        ) : !requests || requests.length === 0 ? (
          <VStack spacing={2} pt={8}>
            <Text fontSize="lg" fontWeight="500" color="text.body.primary">
              No requests yet
            </Text>
            <Text fontSize="sm" color="text.body.secondary">
              Your Luna requests will appear here.
            </Text>
          </VStack>
        ) : (
          <VStack
            w="full"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="8px"
            overflow="hidden"
            spacing={0}
          >
            {requests.map((request, index) => {
              const status = statusConfig[request.status];
              return (
                <Box
                  key={request.sessionId}
                  w="full"
                  px={4}
                  py={3}
                  cursor="pointer"
                  _hover={{ bg: "gray.50" }}
                  borderTop={index > 0 ? "1px solid" : undefined}
                  borderTopColor="gray.200"
                  onClick={() =>
                    navigate(`/luna/requests/${request.sessionId}`)
                  }
                >
                  <HStack justify="space-between" w="full">
                    <HStack spacing={3}>
                      <Tag size="sm" colorScheme={status.colorScheme}>
                        {status.label}
                      </Tag>
                      <Text fontSize="sm" color="text.body.primary">
                        {request.title || "New request"}
                      </Text>
                    </HStack>
                    <Text fontSize="xs" color="text.body.secondary">
                      {formatRelativeTime(request.updatedAt)}
                    </Text>
                  </HStack>
                </Box>
              );
            })}
          </VStack>
        )}
      </Box>
    </VStack>
  );
};

export default LunaRequestsPage;
